import React, { useEffect, useMemo, useState } from 'react';
import { obtenerRevisiones, guardarRevision } from '../services/metasService';
import { revisarSemana } from '../utils/revisionSemana';
import { mensajeAmigable } from '../utils/errores';
import { useToast } from '../context/ToastContext';

const tarjeta = { backgroundColor: 'white', borderRadius: '16px', padding: '16px', border: '1px solid #EEE9F8', boxShadow: '0 1px 3px rgba(60,30,120,.05)', marginBottom: '14px' };
const titulo = { margin: '0 0 8px', fontWeight: 800, fontSize: '16px' };
const fechaCorta = (d) => d.toLocaleDateString('es-HN', { weekday: 'short', day: 'numeric', month: 'short' });

// 🗓️ Revisión de la semana (los viernes): lo logrado, cuánto separar de lo vendido,
// qué ajustar y la nota de la dueña. Rendir cuentas sin regaños: celebra y ajusta.
export default function MetasSemana({ datos, resumen, meta, estrategias, recordatorios, dinero }) {
  const { mostrarToast } = useToast();
  const r = useMemo(() => revisarSemana({ datos, resumen, meta, estrategias, recordatorios, dinero }), [datos, resumen, meta, estrategias, recordatorios, dinero]);
  const [revisiones, setRevisiones] = useState(null);
  const [nota, setNota] = useState('');
  const [marcas, setMarcas] = useState({ separeTuyo: false, separeDeudas: false });
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    obtenerRevisiones().then((l) => {
      setRevisiones(l);
      const esta = l.find((x) => x.semana === r.lunes);
      if (esta) { setNota(esta.nota || ''); setMarcas({ separeTuyo: esta.separe_tuyo, separeDeudas: esta.separe_deudas }); }
    }).catch(() => setRevisiones([]));
  }, [r.lunes]);

  const guardar = async (cambios = {}) => {
    const nuevo = { ...marcas, ...cambios };
    setMarcas(nuevo); setGuardando(true);
    try {
      const fila = await guardarRevision({ semana: r.lunes, nota, ...nuevo });
      setRevisiones((l) => [fila, ...(l || []).filter((x) => x.semana !== fila.semana)]);
      if (!Object.keys(cambios).length) mostrarToast('Tu semana quedó guardada 💜', 'exito');
      else if (Object.values(cambios).some(Boolean)) mostrarToast('¡Bien hecho! Eso es cuidar tu negocio 💪', 'exito');
    } catch (e) { mostrarToast(mensajeAmigable(e), 'error'); } finally { setGuardando(false); }
  };

  const cambio = r.antes > 0 ? Math.round(((r.vendido - r.antes) / r.antes) * 100) : null;
  const pctMeta = r.metaSemana > 0 ? Math.min(100, Math.round((r.vendido / r.metaSemana) * 100)) : null;
  const anterior = (revisiones || []).find((x) => x.semana < r.lunes && x.nota);
  const textoAjuste = (a) => ({
    meta: `Te faltaron ${dinero(a.falta)} para tu meta de la semana.${a.foco ? ` La próxima semana empieza por lo que más se vende (${a.foco}) y por escribirle a tus clientas.` : ' Empieza la semana escribiéndole a tus clientas.'}`,
    dias: `Hubo ${a.n} días de venta sin vender nada: deja una publicación o un mensaje listo para esos días.`,
    tareas: `Cumpliste ${a.hechas} de ${a.total} tareas: agenda menos y cúmplelas todas. Lo que se agenda y se hace, vende.`,
    gasto: `${a.categoria} ya pasó su presupuesto (${a.pct}%): revisa ese gasto antes de la próxima compra.`,
    noAlcanza: 'Lo vendido esta semana no alcanzó para separar todo: primero las tarjetas y lo tuyo, luego los gastos.',
    sinEstrategia: 'No tienes ninguna estrategia en marcha: elige una en Estrategias para la próxima semana.',
  }[a.tipo]);

  return (
    <div>
      {/* ---------- Cómo te fue ---------- */}
      <div style={{ ...tarjeta, background: 'linear-gradient(135deg,#F5F3FF,#FDF2F8)' }}>
        <p style={{ margin: 0, fontSize: '13px', color: '#6B7280', textTransform: 'capitalize' }}>🗓️ Tu semana · {fechaCorta(r.desde)} a {fechaCorta(r.hasta)}</p>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'baseline', flexWrap: 'wrap', margin: '6px 0' }}>
          <span style={{ fontSize: '30px', fontWeight: 800, color: '#5B21B6' }}>{dinero(r.vendido)}</span>
          <span style={{ fontSize: '14px', color: '#374151' }}>{r.nVentas} venta{r.nVentas === 1 ? '' : 's'}{r.ticket ? ` · ${dinero(r.ticket)} cada una` : ''}</span>
          {cambio !== null && <span style={{ fontSize: '13px', fontWeight: 700, color: cambio >= 0 ? '#047857' : '#B91C1C' }}>{cambio >= 0 ? '📈' : '📉'} {cambio >= 0 ? '+' : ''}{cambio}% vs. la semana pasada</span>}
        </div>
        {pctMeta !== null && (
          <>
            <div style={{ height: '10px', background: 'white', borderRadius: '999px', overflow: 'hidden' }}>
              <div style={{ width: `${pctMeta}%`, height: '100%', background: pctMeta >= 100 ? '#059669' : '#7C3AED' }} />
            </div>
            <small style={{ color: '#6B7280' }}>{pctMeta}% de tu meta de la semana ({dinero(r.metaSemana)})</small>
          </>
        )}
        <div style={{ display: 'flex', gap: '4px', marginTop: '12px', alignItems: 'flex-end', height: '60px' }}>
          {r.porDia.map((d) => {
            const max = Math.max(...r.porDia.map((x) => x.total), 1);
            return (
              <div key={d.fecha} title={`${d.nombre}: ${dinero(d.total)}`} style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ height: `${Math.max(3, (d.total / max) * 44)}px`, background: d === r.mejorDia ? '#DB2777' : '#C4B5FD', borderRadius: '6px 6px 2px 2px' }} />
                <small style={{ fontSize: '10px', color: '#6B7280', textTransform: 'capitalize' }}>{d.nombre.slice(0, 3)}</small>
              </div>
            );
          })}
        </div>
      </div>

      {/* ---------- Lo logrado ---------- */}
      {r.logros.length > 0 && (
        <div style={tarjeta}>
          <p style={titulo}>🏆 Lo que lograste</p>
          <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', lineHeight: 1.8 }}>{r.logros.map((l) => <li key={l}>{l}</li>)}</ul>
          <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#6B7280' }}>Mira cuánto avanzaste, no solo lo que falta: eso también es ganar. 🌱</p>
        </div>
      )}

      {/* ---------- Cuánto separar ---------- */}
      {r.separar && r.vendido > 0 && (
        <div style={tarjeta}>
          <p style={titulo}>💵 De lo que vendiste esta semana, separa</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px' }}>
            {[
              ['🛍️ Para mercadería', r.separar.mercaderia, 'reponer lo que vendiste', '#F3F4F6'],
              ['💳 Para tus tarjetas', r.separar.tarjetas, 'tu pago fijo de la semana', '#FEF3C7'],
              [`💜 Lo tuyo (${r.separar.pctTuyo}%)`, r.separar.tuyo, r.separar.pctTuyo <= 1 ? 'empieza con 1 %: lo importante es el hábito' : 'tu ganancia', '#ECFDF5'],
              ['🧾 Para gastos', Math.max(0, r.separar.gastos), 'alquiler, salarios, publicidad…', '#F5F3FF'],
            ].map(([t, v, sub, bg]) => (
              <div key={t} style={{ padding: '12px', borderRadius: '12px', background: bg }}>
                <div style={{ fontSize: '12px', color: '#374151', fontWeight: 700 }}>{t}</div>
                <div style={{ fontSize: '20px', fontWeight: 800 }}>{dinero(v)}</div>
                <div style={{ fontSize: '11px', color: '#6B7280' }}>{sub}</div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '12px', fontSize: '14px' }}>
            <label style={{ display: 'flex', gap: '8px', alignItems: 'center', cursor: 'pointer' }}>
              <input type="checkbox" checked={marcas.separeDeudas} disabled={guardando} onChange={(e) => guardar({ separeDeudas: e.target.checked })} style={{ width: '18px', height: '18px' }} /> Ya separé lo de las tarjetas
            </label>
            <label style={{ display: 'flex', gap: '8px', alignItems: 'center', cursor: 'pointer' }}>
              <input type="checkbox" checked={marcas.separeTuyo} disabled={guardando} onChange={(e) => guardar({ separeTuyo: e.target.checked })} style={{ width: '18px', height: '18px' }} /> Ya separé lo mío
            </label>
          </div>
          <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#6B7280' }}>💡 Pásalo a una cuenta o sobre aparte: si queda mezclado, se gasta.</p>
        </div>
      )}

      {/* ---------- Qué ajustar ---------- */}
      <div style={tarjeta}>
        <p style={titulo}>🔧 Qué ajustar la próxima semana</p>
        {r.ajustes.length ? (
          <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', lineHeight: 1.7 }}>{r.ajustes.map((a) => <li key={a.tipo + (a.categoria || '')}>{textoAjuste(a)}</li>)}</ul>
        ) : <p style={{ margin: 0, fontSize: '14px', color: '#065F46' }}>🎉 ¡Semana redonda! Repite lo que funcionó y sube un poquito la meta.</p>}
      </div>

      {/* ---------- Tu nota ---------- */}
      <div style={tarjeta}>
        <p style={titulo}>✍️ Tu nota de la semana</p>
        <p style={{ margin: '0 0 8px', fontSize: '13px', color: '#6B7280' }}>¿Qué funcionó que puedes repetir? ¿Qué vas a cambiar la próxima semana?</p>
        {anterior && <p style={{ margin: '0 0 8px', fontSize: '12px', color: '#5B21B6', background: '#F5F3FF', padding: '8px 10px', borderRadius: '10px' }}>La semana pasada escribiste: <i>"{anterior.nota}"</i></p>}
        <textarea value={nota} onChange={(e) => setNota(e.target.value.slice(0, 1000))} rows={3} disabled={guardando}
          placeholder="Ej: El video del pedido funcionó; la próxima semana publico uno el martes y escribo a mis clientas el lunes."
          style={{ width: '100%', padding: '10px', borderRadius: '10px', border: '1px solid #D1D5DB', fontSize: '14px', fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical' }} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
          <button className="k-btn-soft" onClick={() => guardar()} disabled={guardando} style={{ background: '#7C3AED', color: 'white', borderColor: '#7C3AED' }}>{guardando ? 'Guardando…' : '💾 Guardar mi semana'}</button>
        </div>
      </div>
    </div>
  );
}
